import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InfoOutlined } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Paper, Tooltip, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDebouncedCallback } from "use-debounce";

import { LoadingButton } from "ui";

import analytics from "fond/analytics";
import {
  selectCurrentAccount,
  useCarveMultiProjectMutation,
  useEstimateCarveMultiProjectQuotaUsageMutation,
  useGetAccountSubscriptionsQuery,
  useGetMultiProjectQuery,
  useGetMultiProjectStatisticsQuery,
} from "fond/api";
import { CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN } from "fond/constants";
import SubLayoutPopout from "fond/layout/SubLayoutPopout/SubLayoutPopout";
import { selectFeature } from "fond/project/redux";
import { useProjectQuota } from "fond/project/solve/hooks/useProjectQuota";
import UsageProgress from "fond/project/solve/UsageProgress";
import { MultiProject, MultiProjectArea, SubscriptionStatus, SystemsOfMeasurement } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { formatNumber } from "fond/utils/number";
import { useStackedNavigationContext } from "fond/widgets";

import { SelectableSubareaList } from "../SelectableSubareaList";

import SelectedSubareaHighlight from "./SelectedSubareaHighlight";
import SubareaSelectionPopout from "./SubareaSelectionPopout";

import { PaperHeader } from "./SubareaListPanel.styles";

const QUOTE_USAGE_ESTIMATION_DEBOUNCE_MS = 250;

interface SubareaListPanelProps {
  multiProject: MultiProject;
}

// TODO - There is a bug with the AgGrid inside the accordion. Sometime it loads without a height or a white overlay across it.
// not sure if accordion or grid is the issue. Can occur on mount

const SubareaListPanel: React.FC<SubareaListPanelProps> = ({ multiProject }: SubareaListPanelProps) => {
  const dispatch = useAppDispatch();
  const { goBack } = useStackedNavigationContext();
  const { isLoading, isFetching } = useGetMultiProjectQuery(multiProject.ID);
  const [carveMultiProject, { isLoading: isCarving, isError }] = useCarveMultiProjectMutation();
  const areasWithProjects = useMemo(() => multiProject?.Areas.filter((area) => area.Project?.ID).map(({ ID }) => ID), [multiProject]);
  const availableAreas = useMemo(() => multiProject?.Areas.filter((area) => !area.Project?.ID), [multiProject]);
  const [selected, setSelected] = useState<string[]>([]);
  const [hoveredAreaId, setHoveredAreaId] = useState<string | null>(null);
  const hasExceededMaxSubareaCount = selected.length > CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN;

  const { data: statistics, isLoading: isLoadingStatistics } = useGetMultiProjectStatisticsQuery(multiProject.ID ?? skipToken);
  const selectedAccount = useAppSelector(selectCurrentAccount);
  const { data: subscriptions } = useGetAccountSubscriptionsQuery(selectedAccount?.ID ?? skipToken);
  const activeSubscription = useMemo(() => subscriptions?.find((sub) => sub.Status === SubscriptionStatus.Active && !!sub.Planner), [subscriptions]);
  const { quota: subscriptionDetails } = useProjectQuota(multiProject);

  const [
    estimateCarveMultiProjectQuotaUsage,
    { data: estimatedQuotaUsage, reset: resetEstimatedQuotaUsage, isLoading: isEstimatingQuotaUsage, isError: isEstimateError },
  ] = useEstimateCarveMultiProjectQuotaUsageMutation();

  // Track the current request so we can abort it when selection changes
  const currentEstimateRequest = useRef<ReturnType<typeof estimateCarveMultiProjectQuotaUsage> | null>(null);

  // Debounced API call to estimate quota usage — prevents rapid requests during "Select All"
  const debouncedEstimate = useDebouncedCallback((selectedAreas: string[]) => {
    currentEstimateRequest.current = estimateCarveMultiProjectQuotaUsage({
      ID: multiProject.ID,
      Areas: selectedAreas.map((ID) => ({ ID })),
    });

    // Swallow the unwrap rejection so it doesn't become an unhandled promise rejection.
    // Abort errors are expected when the user changes selection; real failures are
    // surfaced via the mutation's isError state and shown in the UI.
    currentEstimateRequest.current.unwrap().catch(() => {});
  }, QUOTE_USAGE_ESTIMATION_DEBOUNCE_MS);

  useEffect(
    () => {
      // Abort any in-flight request immediately when selection changes
      if (currentEstimateRequest.current) {
        currentEstimateRequest.current.abort();
        currentEstimateRequest.current = null;
      }

      if (selected.length === 0 || selected.length > CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN) {
        // The backend rejects estimate requests with no selected areas, so clear the data to make sure we're consistent.
        // Also skip estimation if we've exceeded the max count since the user can't proceed anyway.
        resetEstimatedQuotaUsage();
        debouncedEstimate.cancel();
        return;
      }

      resetEstimatedQuotaUsage();
      debouncedEstimate(selected);
    },
    // resetEstimatedQuotaUsage goes infinite if included in the dependency array; really the only
    // thing we actually care about is `selected` changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedEstimate, selected]
  );

  const handleOnClick = async () => {
    if (selected.length === 0) return;
    try {
      await carveMultiProject({ ID: multiProject.ID, Areas: selected.map((ID) => ({ ID })) }).unwrap();
      analytics.track("project.market-lens.plan.projects.created", {
        selectedAreaCount: selected.length,
        totalAreaCount: multiProject.Areas.length,
        previouslyGeneratedAreaCount: areasWithProjects.length,
        projectId: multiProject.ID,
      });
      setSelected([]);
      goBack();
    } catch {
      // The mutation's isError state renders the error alert.
    }
  };

  // Cleanup on unmount: cancel debounced estimate and abort in-flight request
  useEffect(() => {
    return () => {
      debouncedEstimate.cancel();

      if (currentEstimateRequest.current) {
        currentEstimateRequest.current.abort();
        currentEstimateRequest.current = null;
      }
    };
  }, [debouncedEstimate]);

  const handleSelectionChange = (newSelectedIds: string[]) => {
    setSelected(newSelectedIds);
  };

  const handleToggleSelection = useCallback((areaId: string, add: boolean) => {
    setSelected((prev) => (add ? [...prev, areaId] : prev.filter((id) => id !== areaId)));
  }, []);

  const handleAreaClick = useCallback(
    (area: MultiProjectArea) => {
      const feature = {
        type: "Feature",
        id: area.ID,
        source: "multiProject-source",
        properties: { id: area.ID, boundaryId: area.ID, name: area.Name, score: area.Score, type: "subarea" },
        geometry: area.Boundary,
      };
      dispatch(selectFeature(feature, null));
    },
    [dispatch]
  );

  const totalPremCount: number = estimatedQuotaUsage?.EstimatedQuotaUsage ?? 0;
  const availableQuota: number = subscriptionDetails?.AvailableQuota ?? 0;
  const overPremCount = availableQuota - totalPremCount < 0;

  // Treat a non-empty selection with no estimate yet as a loading/blocked state.
  // Only consider it pending if the selection is within the valid range (not 0 and not exceeding max).
  const isEstimatePendingForSelection =
    selected.length > 0 && selected.length <= CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN && estimatedQuotaUsage == null && !isEstimateError;

  const handleRetryEstimate = () => {
    resetEstimatedQuotaUsage();
    debouncedEstimate(selected);
  };

  const gridLoading = isLoading || isFetching || isCarving || isLoadingStatistics;
  const buttonLoading = gridLoading || isEstimatingQuotaUsage || isEstimatePendingForSelection;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100% - 56px)", px: 2, pb: 2 }}>
      <SubLayoutPopout tabId="subarea-config" rect={{ x: 4, y: 38, width: 300, height: 180 }}>
        <SubareaSelectionPopout
          selectedIds={selected}
          onToggleSelection={handleToggleSelection}
          areaStatistics={statistics?.Areas}
          systemOfMeasurement={multiProject?.SystemOfMeasurement ?? SystemsOfMeasurement.Metric}
          projectAreaIds={areasWithProjects}
        />
      </SubLayoutPopout>
      <SelectedSubareaHighlight selectedIds={selected} hoveredAreaId={hoveredAreaId} projectAreaIds={areasWithProjects} />
      <Typography variant="subtitle2" sx={{ mt: 0 }}>
        Confirm subareas
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption">
          Select up to {CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN} subareas for your market lens plan from the list or directly on the map
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
          <PaperHeader variant="h7" fontWeight={500}>
            Selected
          </PaperHeader>
          <Typography variant="h3" component="p" fontWeight={700}>
            {selected.length} / {CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
          <PaperHeader variant="h7" fontWeight={500}>
            Addresses{" "}
            <Tooltip
              title="The total number of addresses after applying the demand model from your selected architecture. In general, this number will be lower than the counts displayed on the map."
              slotProps={{ popper: { sx: { zIndex: 2001 } } }}
            >
              <InfoOutlined color="primary" sx={{ fontSize: 14 }} />
            </Tooltip>
          </PaperHeader>
          <Typography variant="h3" component="p" fontWeight={700}>
            {formatNumber(estimatedQuotaUsage?.EstimatedQuotaUsage ?? 0, 0)}
          </Typography>
        </Paper>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <SelectableSubareaList
          areas={availableAreas}
          areaStatistics={statistics?.Areas}
          selectedIds={selected}
          onSelectionChange={handleSelectionChange}
          onAreaClick={handleAreaClick}
          onHoveredAreaChange={setHoveredAreaId}
          loading={gridLoading}
        />
      </Box>

      {isError && (
        <Alert sx={{ mt: 1 }} severity="error" variant="compact">
          <AlertTitle sx={{ fontSize: (theme) => theme.typography.size.normal, mb: 0 }}>Error creating subarea projects</AlertTitle>
          Something went wrong with creating the subarea projects. Please try again.
        </Alert>
      )}

      {isEstimateError && (
        <Alert
          sx={{ mt: 1 }}
          severity="error"
          variant="compact"
          action={
            <Button color="inherit" size="small" onClick={handleRetryEstimate}>
              Retry
            </Button>
          }
        >
          <AlertTitle sx={{ fontSize: (theme) => theme.typography.size.normal, mb: 0 }}>Estimation failed</AlertTitle>
          Unable to estimate address usage. Please retry or adjust your selection.
        </Alert>
      )}

      {hasExceededMaxSubareaCount && (
        <Alert sx={{ mt: 1 }} severity="error" variant="compact">
          <AlertTitle sx={{ mb: 0 }}>Too many subareas</AlertTitle>
          Projects can be created for up to {CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN} subareas but {selected.length} are currently selected. Please
          reduce the number of subareas selected and try again.
        </Alert>
      )}

      <Box sx={{ mt: 1, height: 180, flexShrink: 0 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={() => goBack()} variant="text" sx={{ flex: 1 }}>
            Cancel
          </Button>
          <LoadingButton
            onClick={handleOnClick}
            variant="contained"
            disabled={buttonLoading || hasExceededMaxSubareaCount || selected.length === 0 || overPremCount}
            sx={{ flex: 1 }}
            size="small"
          >
            Create {selected.length || ""} subarea project{selected.length === 1 ? "" : "s"}
          </LoadingButton>
        </Box>

        {activeSubscription && subscriptionDetails && (
          <UsageProgress subscription={subscriptionDetails} estimatedQuotaUsage={estimatedQuotaUsage} endTime={activeSubscription.EndTime} />
        )}
      </Box>
    </Box>
  );
};

export default SubareaListPanel;
